import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    TablePagination
} from "@mui/material";
import { Close, FilterList, Search } from "@mui/icons-material";
import { THEME_MAP } from "../../theme";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useStateRegions, useStateRegionActions } from "../../actions/stateRegions";
import supabase from "../../services/supabaseClient";

const ORDER_OPTIONS = [
    { value: "contact_date_desc", label: "Contact Date (Newest)" },
    { value: "contact_date_asc", label: "Contact Date (Oldest)" },
    { value: "name_asc", label: "Bar Name (A-Z)" },
    { value: "city_asc", label: "City (A-Z)" }
];

const compareStrings = (a = "", b = "") =>
    a.localeCompare(b, undefined, { sensitivity: "base" });

const parseCityFromAddress = (address) => {
    if (!address || typeof address !== "string") {
        return "";
    }

    const segments = address
        .split(",")
        .map((segment) => segment.trim())
        .filter(Boolean);

    if (segments.length === 0) {
        return "";
    }
    if (segments.length === 1) {
        return segments[0];
    }
    if (segments.length === 2) {
        return segments[0];
    }

    return segments[segments.length - 2] || "";
};

const getDateValue = (row) => {
    if (row.dateInput) {
        const parsed = Date.parse(`${row.dateInput}T00:00:00Z`);
        if (!Number.isNaN(parsed)) {
            return parsed;
        }
    }

    if (row.contacted_by_agent_date) {
        const parsed = Date.parse(row.contacted_by_agent_date);
        if (!Number.isNaN(parsed)) {
            return parsed;
        }
    }

    return null;
};

function normalizeRows(data = []) {
    return data.map((row) => {
        const bar = row.bars || {};
        const currentDate = row.contacted_by_agent_date;
        const barAddress = bar.bar_address || "";
        const osm = bar.osm_metadata && typeof bar.osm_metadata === "object" ? bar.osm_metadata : {};
        const regionPath = Array.isArray(osm.region_id) ? osm.region_id : [];
        return {
            bar_id: row.bar_id,
            bar_name: bar.bar_name || "Unknown bar",
            bar_address: barAddress,
            city: parseCityFromAddress(barAddress),
            stateId: regionPath[0] || "",
            regionId: regionPath[1] || "",
            subregionId: regionPath[2] || "",
            contacted_by_agent: Boolean(row.contacted_by_agent),
            contacted_by_agent_name: row.contacted_by_agent_name || "",
            dateInput: currentDate ? currentDate.slice(0, 10) : "",
            contacted_by_agent_date: currentDate || null,
            contact_notes: row.contact_notes || ""
        };
    });
}

function getRegionLabel(stateRegions, stateId, regionId, subregionId) {
    if (!stateId) return "";
    const state = stateRegions.find((s) => s.id === stateId);
    if (!state) return "";
    const region = (state.regions || []).find((r) => r.id === regionId);
    const regionName = region?.name || "";
    const subregion = (region?.subregions || []).find((s) => s.id === subregionId);
    const subregionName = subregion?.name || "";
    if (regionName && subregionName) return `${regionName} / ${subregionName}`;
    if (regionName) return regionName;
    return "";
}

export default function ContactedBars() {
    const isMobile = useIsMobile();
    const stateRegions = useStateRegions();
    const { fetchStateRegions } = useStateRegionActions();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [savingById, setSavingById] = useState({});
    const [sortOption, setSortOption] = useState("contact_date_desc");
    const [filterModalOpen, setFilterModalOpen] = useState(false);

    const [searchTerm, setSearchTerm] = useState("");
    const [selectedStateId, setSelectedStateId] = useState("");
    const [selectedRegionId, setSelectedRegionId] = useState("");
    const [selectedSubregionId, setSelectedSubregionId] = useState("");
    const [selectedCity, setSelectedCity] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    useEffect(() => {
        fetchStateRegions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const regionOptions = useMemo(() => {
        if (!selectedStateId) return [];
        const state = stateRegions.find((s) => s.id === selectedStateId);
        return (state?.regions || []).filter((r) => r.name && typeof r.name === "string");
    }, [stateRegions, selectedStateId]);

    const subregionOptions = useMemo(() => {
        if (!selectedRegionId) return [];
        const region = regionOptions.find((r) => r.id === selectedRegionId);
        return (region?.subregions || []).filter((s) => s.name && typeof s.name === "string");
    }, [regionOptions, selectedRegionId]);

    const cityOptions = useMemo(() => {
        const collator = new Intl.Collator(undefined, { sensitivity: "base" });
        const map = new Map();
        rows.forEach((row) => {
            const city = (row.city || "").trim();
            if (!city) return;
            const key = city.toLowerCase();
            if (!map.has(key)) {
                map.set(key, { key, label: city });
            }
        });
        return Array.from(map.values()).sort((a, b) => collator.compare(a.label, b.label));
    }, [rows]);

    const handleStateChange = useCallback((event) => {
        setSelectedStateId(event.target.value);
        setSelectedRegionId("");
        setSelectedSubregionId("");
        setPage(0);
    }, []);

    const handleRegionChange = useCallback((event) => {
        setSelectedRegionId(event.target.value);
        setSelectedSubregionId("");
        setPage(0);
    }, []);

    const handleSubregionChange = useCallback((event) => {
        setSelectedSubregionId(event.target.value);
        setPage(0);
    }, []);

    const handleCityChange = useCallback((_event, option) => {
        setSelectedCity(option);
        setPage(0);
    }, []);

    const handleClearFilters = useCallback(() => {
        setSearchTerm("");
        setSelectedStateId("");
        setSelectedRegionId("");
        setSelectedSubregionId("");
        setSelectedCity(null);
        setPage(0);
    }, []);

    const activeFilters = useMemo(() => {
        const filters = [];
        if (searchTerm.trim()) {
            filters.push({ type: "search", label: `Search: "${searchTerm.trim()}"` });
        }
        if (selectedStateId) {
            const state = stateRegions.find((s) => s.id === selectedStateId);
            filters.push({ type: "state", label: `State: ${state?.name || selectedStateId}` });
        }
        if (selectedRegionId) {
            const region = regionOptions.find((r) => r.id === selectedRegionId);
            filters.push({ type: "region", label: `Region: ${region?.name || selectedRegionId}` });
        }
        if (selectedSubregionId) {
            const sub = subregionOptions.find((s) => s.id === selectedSubregionId);
            filters.push({ type: "subregion", label: `Subregion: ${sub?.name || selectedSubregionId}` });
        }
        if (selectedCity) {
            filters.push({ type: "city", label: `City: ${selectedCity.label}` });
        }
        return filters;
    }, [searchTerm, selectedStateId, selectedRegionId, selectedSubregionId, selectedCity, stateRegions, regionOptions, subregionOptions]);

    const handleRemoveFilter = useCallback((type) => {
        switch (type) {
            case "search": setSearchTerm(""); break;
            case "state": setSelectedStateId(""); setSelectedRegionId(""); setSelectedSubregionId(""); break;
            case "region": setSelectedRegionId(""); setSelectedSubregionId(""); break;
            case "subregion": setSelectedSubregionId(""); break;
            case "city": setSelectedCity(null); break;
            default: break;
        }
        setPage(0);
    }, []);

    const filteredRows = useMemo(() => {
        let result = rows;

        const term = searchTerm.trim().toLowerCase();
        if (term) {
            result = result.filter((row) =>
                (row.bar_name && row.bar_name.toLowerCase().includes(term)) ||
                (row.city && row.city.toLowerCase().includes(term)) ||
                (row.bar_address && row.bar_address.toLowerCase().includes(term)) ||
                (row.contacted_by_agent_name && row.contacted_by_agent_name.toLowerCase().includes(term)) ||
                (row.contact_notes && row.contact_notes.toLowerCase().includes(term))
            );
        }

        if (selectedStateId) {
            const state = stateRegions.find((s) => s.id === selectedStateId);
            const stateValues = new Set([selectedStateId, state?.abbr, state?.name].filter(Boolean));
            result = result.filter((row) => row.stateId && stateValues.has(row.stateId));
        }

        if (selectedRegionId) {
            result = result.filter((row) => row.regionId === selectedRegionId);
        }

        if (selectedSubregionId) {
            result = result.filter((row) => row.subregionId === selectedSubregionId);
        }

        if (selectedCity) {
            const cityLower = selectedCity.key;
            result = result.filter((row) => (row.city || "").toLowerCase() === cityLower);
        }

        return result;
    }, [rows, searchTerm, selectedStateId, selectedRegionId, selectedSubregionId, selectedCity, stateRegions]);

    const sortedRows = useMemo(() => {
        const copy = [...filteredRows];

        switch (sortOption) {
            case "name_asc":
                copy.sort((a, b) => compareStrings(a.bar_name, b.bar_name));
                break;
            case "city_asc":
                copy.sort((a, b) => {
                    const cityComparison = compareStrings(a.city, b.city);
                    if (cityComparison !== 0) {
                        return cityComparison;
                    }
                    return compareStrings(a.bar_name, b.bar_name);
                });
                break;
            case "contact_date_asc":
                copy.sort((a, b) => {
                    const aValue = getDateValue(a);
                    const bValue = getDateValue(b);

                    if (aValue === null && bValue === null) {
                        return 0;
                    }
                    if (aValue === null) {
                        return 1;
                    }
                    if (bValue === null) {
                        return -1;
                    }
                    return aValue - bValue;
                });
                break;
            case "contact_date_desc":
            default:
                copy.sort((a, b) => {
                    const aValue = getDateValue(a);
                    const bValue = getDateValue(b);

                    if (aValue === null && bValue === null) {
                        return 0;
                    }
                    if (aValue === null) {
                        return 1;
                    }
                    if (bValue === null) {
                        return -1;
                    }
                    return bValue - aValue;
                });
                break;
        }

        return copy;
    }, [filteredRows, sortOption]);

    const hasRows = sortedRows.length > 0;

    const paginatedRows = useMemo(() => {
        const start = page * rowsPerPage;
        return sortedRows.slice(start, start + rowsPerPage);
    }, [sortedRows, page, rowsPerPage]);

    const loadRows = useCallback(async () => {
        setLoading(true);
        setError("");
        setSuccess("");
        const { data, error: fetchError } = await supabase
            .from("reviewed_and_contacted_bars")
            .select("bar_id, contacted_by_agent, contacted_by_agent_date, contacted_by_agent_name, contact_notes, bars(bar_name, bar_address, osm_metadata)")
            .order("contacted_by_agent", { ascending: true })
            .order("contacted_by_agent_date", { ascending: false, nullsFirst: false });

        if (fetchError) {
            setError(fetchError.message || "Unable to load contacted bars right now.");
            setRows([]);
        } else {
            setRows(normalizeRows(data || []));
            setPage(0);
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        loadRows();
    }, [loadRows]);

    useEffect(() => {
        const maxPage = Math.max(0, Math.ceil(sortedRows.length / rowsPerPage) - 1);
        if (page > maxPage) {
            setPage(maxPage);
        }
    }, [sortedRows.length, rowsPerPage, page]);

    const updateLocalRow = (barId, updates) => {
        setRows((prev) =>
            prev.map((row) =>
                row.bar_id === barId
                    ? {
                          ...row,
                          ...updates
                      }
                    : row
            )
        );
    };

    const handleFieldChange = (barId, field, value) => {
        updateLocalRow(barId, { [field]: value });
        setSuccess("");
    };

    const handleToggleContacted = (barId) => {
        setRows((prev) =>
            prev.map((row) =>
                row.bar_id === barId
                    ? {
                          ...row,
                          contacted_by_agent: !row.contacted_by_agent
                      }
                    : row
            )
        );
        setSuccess("");
    };

    const handleSave = async (row) => {
        setSavingById((prev) => ({ ...prev, [row.bar_id]: true }));
        setError("");
        setSuccess("");

        const trimmedName = row.contacted_by_agent_name.trim();
        const trimmedNotes = row.contact_notes.trim();
        const dateIso = row.dateInput ? new Date(`${row.dateInput}T00:00:00.000Z`).toISOString() : null;

        const { error: updateError } = await supabase
            .from("reviewed_and_contacted_bars")
            .update({
                contacted_by_agent: row.contacted_by_agent,
                contacted_by_agent_name: trimmedName || null,
                contacted_by_agent_date: dateIso,
                contact_notes: trimmedNotes || null
            })
            .eq("bar_id", row.bar_id);

        if (updateError) {
            setError(updateError.message || "Unable to update this record. Please try again.");
        } else {
            updateLocalRow(row.bar_id, {
                contacted_by_agent_name: trimmedName,
                contacted_by_agent_date: dateIso,
                dateInput: row.dateInput,
                contact_notes: trimmedNotes
            });
            setSuccess("Contact details saved.");
        }

        setSavingById((prev) => {
            const next = { ...prev };
            delete next[row.bar_id];
            return next;
        });
    };

    const filterControls = (
        <>
            <FormControl className="region-select" variant="outlined" size="small" fullWidth={isMobile}>
                <InputLabel id="cb-state-label">State</InputLabel>
                <Select
                    labelId="cb-state-label"
                    label="State"
                    value={selectedStateId}
                    onChange={handleStateChange}
                >
                    <MenuItem value="">All States</MenuItem>
                    {stateRegions
                        .filter((s) => s.name && typeof s.name === "string")
                        .map((s) => (
                            <MenuItem key={s.id} value={s.id}>
                                {s.name}{s.abbr ? ` (${s.abbr})` : ""}
                            </MenuItem>
                        ))}
                </Select>
            </FormControl>

            <FormControl className="region-select" variant="outlined" size="small" fullWidth={isMobile}>
                <InputLabel id="cb-region-label">Region</InputLabel>
                <Select
                    labelId="cb-region-label"
                    label="Region"
                    value={selectedRegionId}
                    onChange={handleRegionChange}
                    disabled={!selectedStateId}
                >
                    <MenuItem value="">All Regions</MenuItem>
                    {regionOptions.map((r) => (
                        <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
                    ))}
                </Select>
            </FormControl>

            <FormControl className="region-select" variant="outlined" size="small" fullWidth={isMobile}>
                <InputLabel id="cb-subregion-label">Subregion</InputLabel>
                <Select
                    labelId="cb-subregion-label"
                    label="Subregion"
                    value={selectedSubregionId}
                    onChange={handleSubregionChange}
                    disabled={!selectedStateId || !selectedRegionId}
                >
                    <MenuItem value="">All Subregions</MenuItem>
                    {subregionOptions.map((s) => (
                        <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                    ))}
                </Select>
            </FormControl>

            <Autocomplete
                className="city-autocomplete"
                options={cityOptions}
                getOptionLabel={(option) => option?.label || ""}
                isOptionEqualToValue={(option, value) => option?.key === value?.key}
                value={selectedCity}
                onChange={handleCityChange}
                clearOnEscape
                size="small"
                fullWidth={isMobile}
                renderInput={(params) => (
                    <TextField {...params} label="City" placeholder="Filter by city" variant="outlined" />
                )}
            />
        </>
    );

    return (
        <Container>
            {isMobile ? (
                <MobileSearchRow>
                    <TextField
                        type="search"
                        size="small"
                        placeholder="Search bars"
                        value={searchTerm}
                        onChange={(event) => {
                            setSearchTerm(event.target.value);
                            setPage(0);
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search fontSize="small" />
                                </InputAdornment>
                            )
                        }}
                        className="search-field"
                    />
                    <IconButton
                        onClick={() => setFilterModalOpen(true)}
                        className="filter-icon-btn"
                        size="small"
                    >
                        <FilterList />
                        {activeFilters.filter((f) => f.type !== "search").length > 0 && (
                            <span className="filter-badge">
                                {activeFilters.filter((f) => f.type !== "search").length}
                            </span>
                        )}
                    </IconButton>

                    <Dialog
                        open={filterModalOpen}
                        onClose={() => setFilterModalOpen(false)}
                        fullWidth
                        maxWidth="sm"
                        PaperProps={{ sx: { borderRadius: "12px" } }}
                    >
                        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
                            Sort &amp; Filter
                            <IconButton size="small" onClick={() => setFilterModalOpen(false)}>
                                <Close />
                            </IconButton>
                        </DialogTitle>
                        <DialogContent>
                            <ModalFilterStack>
                                <FormControl size="small" fullWidth>
                                    <InputLabel id="contacted-bars-order-label-mobile">Order By</InputLabel>
                                    <Select
                                        labelId="contacted-bars-order-label-mobile"
                                        value={sortOption}
                                        label="Order By"
                                        onChange={(event) => setSortOption(event.target.value)}
                                    >
                                        {ORDER_OPTIONS.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                {filterControls}
                            </ModalFilterStack>
                        </DialogContent>
                        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                            <Button variant="outlined" size="small" onClick={() => { handleClearFilters(); setFilterModalOpen(false); }}>
                                Clear All
                            </Button>
                            <Button variant="contained" size="small" onClick={() => setFilterModalOpen(false)}>
                                Done
                            </Button>
                        </DialogActions>
                    </Dialog>
                </MobileSearchRow>
            ) : (
                <FilterRow>
                    <TextField
                        className="search-field"
                        label="Search Bars"
                        placeholder="Search by name"
                        variant="outlined"
                        size="small"
                        value={searchTerm}
                        onChange={(event) => {
                            setSearchTerm(event.target.value);
                            setPage(0);
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search fontSize="small" />
                                </InputAdornment>
                            )
                        }}
                    />
                    {filterControls}
                    <FormControl size="small" className="sort-select">
                        <InputLabel id="contacted-bars-order-label">Order By</InputLabel>
                        <Select
                            labelId="contacted-bars-order-label"
                            id="contacted-bars-order"
                            value={sortOption}
                            label="Order By"
                            onChange={(event) => setSortOption(event.target.value)}
                        >
                            {ORDER_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Button variant="outlined" onClick={loadRows} disabled={loading} className="refresh-btn">
                        Refresh
                    </Button>
                </FilterRow>
            )}

            {activeFilters.length > 0 && (
                <ChipsRow>
                    {activeFilters.map((filter) => (
                        <Chip
                            key={filter.type}
                            label={filter.label}
                            onDelete={() => handleRemoveFilter(filter.type)}
                            deleteIcon={<Close />}
                            size="small"
                            className="filter-chip"
                        />
                    ))}
                </ChipsRow>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: isMobile ? 2 : 3 }}>
                    {error}
                </Alert>
            )}
            {success && (
                <Alert severity="success" sx={{ mb: isMobile ? 2 : 3 }}>
                    {success}
                </Alert>
            )}

            {loading ? (
                <Box display="flex" justifyContent="center" mt={4}>
                    <CircularProgress />
                </Box>
            ) : !hasRows ? (
                <Typography variant="body1">No contacted bars found.</Typography>
            ) : isMobile ? (
                <>
                    {paginatedRows.map((row) => {
                        const isSaving = Boolean(savingById[row.bar_id]);
                        return (
                            <MobileCard key={row.bar_id}>
                                <div className="card-header">
                                    <strong>{row.bar_name}</strong>
                                    {row.bar_address && (
                                        <div className="muted">{row.bar_address}</div>
                                    )}
                                    {getRegionLabel(stateRegions, row.stateId, row.regionId, row.subregionId) && (
                                        <div className="muted region-label">{getRegionLabel(stateRegions, row.stateId, row.regionId, row.subregionId)}</div>
                                    )}
                                </div>

                                <div className="card-row">
                                    <Checkbox
                                        color="primary"
                                        checked={row.contacted_by_agent}
                                        onChange={() => handleToggleContacted(row.bar_id)}
                                        sx={{ p: 0, mr: 1 }}
                                    />
                                    <span className="card-label">Contacted</span>
                                </div>

                                <TextField
                                    value={row.contacted_by_agent_name}
                                    onChange={(event) =>
                                        handleFieldChange(row.bar_id, "contacted_by_agent_name", event.target.value)
                                    }
                                    placeholder="Sales Person"
                                    size="small"
                                    fullWidth
                                />

                                <TextField
                                    type="date"
                                    value={row.dateInput}
                                    onChange={(event) =>
                                        handleFieldChange(row.bar_id, "dateInput", event.target.value)
                                    }
                                    size="small"
                                    fullWidth
                                    label="Contact Date"
                                    InputLabelProps={{ shrink: true }}
                                />

                                <TextField
                                    value={row.contact_notes}
                                    onChange={(event) =>
                                        handleFieldChange(row.bar_id, "contact_notes", event.target.value)
                                    }
                                    placeholder="Notes"
                                    size="small"
                                    fullWidth
                                    multiline
                                    minRows={2}
                                />

                                <Button
                                    variant="contained"
                                    color="primary"
                                    size="small"
                                    onClick={() => handleSave(row)}
                                    disabled={isSaving}
                                    fullWidth
                                >
                                    {isSaving ? "Saving..." : "Save"}
                                </Button>
                            </MobileCard>
                        );
                    })}
                    <TablePagination
                        component="div"
                        count={sortedRows.length}
                        page={page}
                        onPageChange={(_, newPage) => setPage(newPage)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(event) => {
                            setRowsPerPage(Number(event.target.value));
                            setPage(0);
                        }}
                        rowsPerPageOptions={[25, 50, 100, 250]}
                        labelRowsPerPage="Rows"
                    />
                </>
            ) : (
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Bar</TableCell>
                                <TableCell>Contacted?</TableCell>
                                <TableCell>Sales Person</TableCell>
                                <TableCell>Contact Date</TableCell>
                                <TableCell>Notes</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedRows.map((row) => {
                                const isSaving = Boolean(savingById[row.bar_id]);
                                return (
                                    <TableRow key={row.bar_id} hover>
                                        <TableCell>
                                            <strong>{row.bar_name}</strong>
                                            {row.bar_address && (
                                                <div className="muted">{row.bar_address}</div>
                                            )}
                                            {getRegionLabel(stateRegions, row.stateId, row.regionId, row.subregionId) && (
                                                <div className="muted region-label">{getRegionLabel(stateRegions, row.stateId, row.regionId, row.subregionId)}</div>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Checkbox
                                                color="primary"
                                                checked={row.contacted_by_agent}
                                                onChange={() => handleToggleContacted(row.bar_id)}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <TextField
                                                value={row.contacted_by_agent_name}
                                                onChange={(event) =>
                                                    handleFieldChange(
                                                        row.bar_id,
                                                        "contacted_by_agent_name",
                                                        event.target.value
                                                    )
                                                }
                                                placeholder="Name"
                                                size="small"
                                                fullWidth
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <TextField
                                                type="date"
                                                value={row.dateInput}
                                                onChange={(event) =>
                                                    handleFieldChange(
                                                        row.bar_id,
                                                        "dateInput",
                                                        event.target.value
                                                    )
                                                }
                                                size="small"
                                                fullWidth
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <TextField
                                                value={row.contact_notes}
                                                onChange={(event) =>
                                                    handleFieldChange(
                                                        row.bar_id,
                                                        "contact_notes",
                                                        event.target.value
                                                    )
                                                }
                                                placeholder="Add notes"
                                                size="small"
                                                fullWidth
                                                multiline
                                                minRows={2}
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                size="small"
                                                onClick={() => handleSave(row)}
                                                disabled={isSaving}
                                            >
                                                {isSaving ? "Saving..." : "Save"}
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={sortedRows.length}
                        page={page}
                        onPageChange={(_, newPage) => setPage(newPage)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(event) => {
                            setRowsPerPage(Number(event.target.value));
                            setPage(0);
                        }}
                        rowsPerPageOptions={[25, 50, 100, 250]}
                        labelRowsPerPage="Rows per page"
                    />
                </TableContainer>
            )}
        </Container>
    );
}

const Container = styled.div`
    height: 100%;
    width: 100%;
    overflow: auto;
    padding: 24px;
    background: ${THEME_MAP["color-basic-100"]};
    display: flex;
    flex-direction: column;
    gap: 24px;

    .muted {
        font-size: 0.75rem;
        color: rgba(0, 0, 0, 0.6);
    }

    .region-label {
        font-style: italic;
    }

    @media (max-width: 767px) {
        padding: 16px;
        gap: 12px;
    }
`;

const FilterRow = styled.div`
    display: flex;
    gap: 10px;
    align-items: center;
    flex-wrap: nowrap;
    background: ${THEME_MAP["color-basic-100"]};
    border-radius: 8px;
    padding: 12px 8px 12px 0;
    overflow-x: auto;

    .search-field {
        min-width: 200px;
        max-width: 250px;
    }

    .region-select {
        min-width: 160px;
        max-width: 180px;
    }

    .city-autocomplete {
        min-width: 150px;
        max-width: 170px;
    }

    .sort-select {
        min-width: 180px;
        max-width: 220px;
    }

    .refresh-btn {
        white-space: nowrap;
        margin-left: auto;
    }
`;

const ChipsRow = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;

    .filter-chip {
        background: ${THEME_MAP["color-primary-100"]};
        color: ${THEME_MAP["color-primary-700"]};
        font-weight: 500;
    }
`;

const MobileSearchRow = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;

    .search-field {
        flex: 1;
        min-width: 0;
    }

    .filter-icon-btn {
        position: relative;
        color: ${THEME_MAP["color-basic-700"]};
        flex-shrink: 0;
    }
`;

const ModalFilterStack = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding-top: 8px;
`;

const MobileCard = styled.div`
    background: ${THEME_MAP["color-basic-100"]};
    border: 1px solid ${THEME_MAP["color-basic-400"]};
    border-radius: 8px;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;

    .card-header {
        strong {
            font-size: 0.95rem;
        }
    }

    .card-row {
        display: flex;
        align-items: center;
    }

    .card-label {
        font-size: 0.875rem;
        color: ${THEME_MAP["color-basic-700"]};
    }
`;
